import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

/**
 * Floating "back to top" button — appears once the user has scrolled past
 * ~480px. Mirrors the JapanETCcard.com-style purple FAB pinned to the
 * bottom-right corner. Honors prefers-reduced-motion (instant jump) and
 * respects the mobile safe-area inset.
 */
export default function ScrollToTopButton({ lang }) {
  const [visible, setVisible] = useState(false);
  const isJa = lang === "ja";

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = () => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="scroll-to-top-button"
      aria-label={isJa ? "ページ上部に戻る" : "Back to top"}
      title={isJa ? "ページ上部に戻る" : "Back to top"}
      className={[
        "group fixed z-40",
        "right-4 sm:right-6",
        "inline-flex items-center justify-center",
        "h-12 w-12 sm:h-14 sm:w-14 rounded-full",
        "bg-[#5B2E83] hover:bg-[#3F1F5B] active:bg-[#2D1542]",
        "text-white",
        "shadow-[0_12px_28px_-10px_rgba(91,46,131,0.6)]",
        "transition-all duration-300",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#5B2E83]/30",
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none",
      ].join(" ")}
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
      }}
    >
      {/* Ping ring on hover for affordance */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full border border-[#7AB8E8]/50 opacity-0 group-hover:opacity-100 group-hover:animate-brand-ping-ring origin-center"
        style={{ transformOrigin: "center" }}
      />
      <ArrowUp className="relative h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={2.2} />
    </button>
  );
}
